import { action, computed, makeAutoObservable } from 'mobx';
import { cloneDeep } from 'lodash';
import { TopEventsEventsState } from 'src/AppState/CmsState/TopEventsState/TopEventsState';
import { arrayMove } from 'react-movable';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getAllTopEventsSports';
import { TrpcClient } from 'src/AppState/TrpcClient';

type TopEventsSportDataModelType = Response200Type[number];

export class TopEventsSportsListState {
    public constructor(
        private readonly trpcClient: TrpcClient,
        private topEventsEventsSectionState: TopEventsEventsState
    ) {
        this.topEventsEventsSectionState.setSportsDataFromResource();
        makeAutoObservable(this);
    }

    @computed public get sportsArrayForDraggableList(): Array<TopEventsSportDataModelType> {
        return this.topEventsEventsSectionState.sportsArray;
    }

    private filterElements = (sport: TopEventsSportDataModelType, oldIndex: number, newIndex: number): boolean => {
        if (oldIndex > newIndex) {
            return sport.display_order >= newIndex && sport.display_order < oldIndex;
        } else {
            return sport.display_order <= newIndex && sport.display_order > oldIndex;
        }
    };

    private updateDisplayOrder = (
        arrayMoved: Array<TopEventsSportDataModelType>,
        oldIndex: number,
        newIndex: number
    ): Array<TopEventsSportDataModelType> => {
        const tempMovedArray = cloneDeep(arrayMoved);
        const topEventsSportsToMove = arrayMoved
            .filter((sport) => this.filterElements(sport, oldIndex, newIndex))
            .map((c) => c.id);
        const shouldIncrementIndexes = oldIndex > newIndex;
        const draggedSport = arrayMoved.find((sport) => sport.display_order === oldIndex);

        for (let i = 0; i < tempMovedArray.length; i++) {
            const sport = tempMovedArray[i];
            if (sport === undefined) {
                return tempMovedArray;
            }
            if (topEventsSportsToMove.includes(sport.id)) {
                if (shouldIncrementIndexes) {
                    sport.display_order = sport.display_order + 1;
                } else {
                    sport.display_order = sport.display_order - 1;
                }
            }

            if (sport.id === draggedSport?.id) {
                sport.display_order = newIndex;
            }
        }
        return tempMovedArray;
    };

    @action public handleSportsOrder = async ({
        oldIndex,
        newIndex,
    }: {
        oldIndex: number;
        newIndex: number;
    }): Promise<void> => {
        const displayOrderOfOldIndexElement = this.topEventsEventsSectionState.sportsArray[oldIndex]?.display_order;
        const displayOrderOfNewIndexElement = this.topEventsEventsSectionState.sportsArray[newIndex]?.display_order;
        const draggedSport = this.topEventsEventsSectionState.sportsArray[oldIndex];

        if (displayOrderOfNewIndexElement === undefined || displayOrderOfOldIndexElement === undefined) {
            return;
        }

        const arrayMoved = arrayMove(this.topEventsEventsSectionState.sportsArray, oldIndex, newIndex);
        const arrayWithUpdatedDisplayOrder = this.updateDisplayOrder(
            arrayMoved,
            displayOrderOfOldIndexElement,
            displayOrderOfNewIndexElement
        );
        this.topEventsEventsSectionState.setSportsData(arrayWithUpdatedDisplayOrder);

        if (draggedSport === undefined) {
            return;
        }

        await this.trpcClient.client.topEventsSports.changeTopEventsSportsOrder.mutate({
            data: {
                id: draggedSport.id,
                new_order: displayOrderOfNewIndexElement,
                old_order: displayOrderOfOldIndexElement,
            },
        });

        await this.topEventsEventsSectionState.topEventsEventsResource.refresh();
    };
}
